// Step 5d Phase 5 — rolling prefetch (deliberate, window-independent cache coverage).
//
// Fetches the home −2/+7d bookings window + the booking-form reference collections through
// the EXISTING tenant-scoped reads (no new endpoint, no base-broadening) and write-throughs
// the Phase-4 encrypted cache. Runs only when ONLINE and UNLOCKED (DEK present); otherwise a
// no-op. Sparseness (S24 lesson — never a fetch storm): a module-level in-flight guard
// prevents overlap, and a per-scope freshness gate skips a scope refreshed within
// FRESH_SKIP_MS — so the poll/focus-piggybacked pass mostly no-ops, while reconnect/initial
// passes force a refresh. Pinned scopes refresh alongside the home window.

import { apiFetch } from '../api';
import { getDek } from './dekSession';
import { getConnectionOnline } from '../connection';
import {
  bookingsScopeKey, saveBookingsScope, saveReference, scopeAgeMs, getBookingsCursor, getPinnedScopes,
} from './cache';
import { fetchChangesSince } from './changefeed';
import { REF_SPEC } from './refSpec';
import type { Booking } from '../opsBoardTypes';

// A scope refreshed more recently than this is left alone on a non-forced (poll-piggyback)
// pass — keeps the rolling refresh to ~one wide fetch per interval during continuous use,
// while reconnect/initial passes force it. (Poll cadence is 45 s; this is comfortably above.)
const FRESH_SKIP_MS = 4 * 60 * 1000;
// Gentle fan-out, mirroring OpsBoard's REF_BATCH_SIZE. PERF PART B: prod connection_limit confirmed as 10
// by Ben from Railway on 2026-09-15 (it was 3 on the belief it was 1). 6, not 10: the board's other
// requests draw from the same pool at the same time.
const REF_BATCH = 6;
const enc = encodeURIComponent;

let prefetching = false;

export interface PrefetchWindow { from: string; to: string }

// Fetch one bookings window cache-only (carrying the change-feed cursor exactly as
// loadBookings does). force=true bypasses the freshness gate. Returns whether it fetched.
async function prefetchBookingsWindow(from: string, to: string, force: boolean): Promise<boolean> {
  const key = bookingsScopeKey(null, from, to);
  if (!force) {
    const age = await scopeAgeMs(key);
    if (age !== null && age < FRESH_SKIP_MS) return false; // recently refreshed — skip (dedup)
  }
  const cursor = await getBookingsCursor(key);
  const feed = await fetchChangesSince(cursor); // tolerant; advances/keeps the cursor
  const res = await apiFetch<Booking[]>(`/api/bookings?from=${enc(from)}&to=${enc(to)}`);
  if (res.error || !res.data) return false;
  await saveBookingsScope(key, { from, to }, res.data, feed.headCursor ?? undefined);
  return true;
}

/** Force-cache an arbitrary window (picker pin + inventory per-scope "refresh now"). */
export async function cacheBookingsWindow(from: string, to: string): Promise<boolean> {
  if (!getConnectionOnline() || !getDek()) return false;
  return prefetchBookingsWindow(from, to, true);
}

// Reference collections — same set + keys as the board's saveRefDataToCache (cache-only here;
// no React state). Gated on the ref:bases scope freshness so it stays sparse. The list itself
// lives in refSpec.ts so the offline-cache tier gate can import it (0.12.7 Part E).

// ── PERF PART A (2026-09-15) — the board's write-through counts as a refresh ────────────────────
// The board's loadRefData fetches exactly the REF_SPEC set and writes it to this cache (OpsBoard
// saveRefDataToCache). The mount/window-change prefetch then fetched the same eight endpoints AGAIN
// seconds later, because it forced past the freshness gate below (prod: every reference endpoint
// appeared exactly twice per load, ~930 ms per query). The data was already in hand.
//
// That write-through is fire-and-forget so the board never waits on IndexedDB, which means its
// `refreshed_at` stamp may not have landed when this gate reads it. So the board registers the
// in-flight write here, and an UNFORCED reference pass waits for it before reading the stamp. The
// board itself still never waits. If the write failed, saveReference swallows it without stamping,
// the scope stays stale, and this pass fetches as it always did — coverage can only be the same.
let referenceWriteThrough: Promise<unknown> | null = null;
export function noteReferenceWriteThrough(p: Promise<unknown>): void {
  referenceWriteThrough = p.catch(() => undefined);
}

async function prefetchReference(force: boolean): Promise<void> {
  if (!force) {
    if (referenceWriteThrough) await referenceWriteThrough;
    const age = await scopeAgeMs('ref:bases');
    if (age !== null && age < FRESH_SKIP_MS) return;
  }
  for (let i = 0; i < REF_SPEC.length; i += REF_BATCH) {
    const batch = REF_SPEC.slice(i, i + REF_BATCH);
    await Promise.all(batch.map(async (r) => {
      const res = await apiFetch<any>(r.path);
      if (res.error || res.data == null) return;
      const items = r.singleton ? [res.data] : (res.data ?? []);
      await saveReference(r.collection, items, r.keyOf);
    }));
  }
}

export interface RollingPrefetchArgs {
  /** Home −2/+7d window (computed by the caller from the home base tz); null → skip the auto window. */
  homeWindow: PrefetchWindow | null;
  /** Bypass the freshness gate (use on reconnect / explicit "refresh now"). */
  force?: boolean;
  /**
   * PERF PART A: force the REFERENCE half separately from the bookings windows. Defaults to `force`,
   * so every existing caller (reconnect, the offline cache panel, the poll tick) is unchanged. Only
   * the board's mount/window-change pass sets it false, because loadRefData has just fetched and
   * written the same set — the bookings windows there stay forced.
   */
  forceReference?: boolean;
}

/**
 * The rolling refresh: home window ∪ pinned scopes + reference. Guarded against overlap and
 * skipped offline/locked. Safe to call freely (initial load, reconnect, poll tick) — it
 * self-throttles via the in-flight guard + freshness gate.
 */
export async function rollingPrefetch(args: RollingPrefetchArgs): Promise<void> {
  if (prefetching) return;
  if (!getConnectionOnline() || !getDek()) return; // online + unlocked only (locked #7)
  prefetching = true;
  try {
    const windows: PrefetchWindow[] = [];
    if (args.homeWindow) windows.push(args.homeWindow);
    for (const p of await getPinnedScopes()) windows.push({ from: p.from, to: p.to });
    const seen = new Set<string>();
    for (const w of windows) {
      const k = `${w.from}|${w.to}`;
      if (seen.has(k)) continue; // home may coincide with a pin — fetch once
      seen.add(k);
      await prefetchBookingsWindow(w.from, w.to, !!args.force);
    }
    await prefetchReference(args.forceReference ?? !!args.force);
  } finally {
    prefetching = false;
  }
}
